<script>
import { Bar } from 'vue-chartjs';
import { mapState } from 'vuex';
import { getFormattedCurrentDate } from '@/utils.js';

export default {
  extends: Bar,
  props: {
    datasets: {
      type: Array,
      default: () => []
    },
    isDailyData: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      PREFERRED_STEPS_MS: [
        5 * 60 * 1000, // 5 minuti
        15 * 60 * 1000, // 15 minuti
        30 * 60 * 1000, // 30 minuti
        60 * 60 * 1000, // 1 ora
        3 * 60 * 60 * 1000, // 3 ore
        6 * 60 * 60 * 1000, // 6 ore
        12 * 60 * 60 * 1000, // 12 ore
        24 * 60 * 60 * 1000 // 24 ore
      ],
      chartdata: {},
      mouseEntered: false,
      arrowCanvas: null,
      arrowImage: {},
      options: {
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          enabled: true,
          mode: 'index',
          intersect: false,
          // filter: (tooltipItem, data) => {
          //   return !data.datasets[tooltipItem.datasetIndex].layerName?.includes('DIR');
          // },
          callbacks: {
            title: (tooltipItems) => {
              if (!tooltipItems || !tooltipItems.length || !tooltipItems[0] || tooltipItems[0].xLabel == null) {
                return '';
              }

              return this.getFormattedDate(tooltipItems[0].xLabel);
            },
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];

              if (!dataset) {
                return null;
              }

              const value = dataset.data[tooltipItem.index];

              if (value === null || value === undefined || isNaN(value)) {
                return null;
              }

              return `${dataset.label}: ${value}`;
            }
          }
        },
        hover: {
          mode: 'index',
          intersect: false,
          animationDuration: 0
        },
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              id: 'y-axis-1',
              position: 'left',
              ticks: {
                display: true
              },
              scaleLabel: {
                display: true,
                labelString: this.datasets[0]?.label || ''
              }
            },
            {
              id: 'y-axis-2',
              display: this.datasets.length > 1,
              position: 'right',
              ticks: {
                display: true
              },
              scaleLabel: {
                display: true,
                labelString: this.datasets.length > 1 ? this.datasets[1].label : ''
              },
              gridLines: {
                drawTicks: false,
                display: false
              }
            }
          ],
          xAxes: [
            {
              id: 'x-axis-1',
              ticks: {
                autoSkip: false,
                display: true
              },
              gridLines: {
                drawTicks: true,
                display: false
              }
            }
          ]
        }
      }
    };
  },
  computed: {
    ...mapState(['table_chart'])
  },
  mounted() {
    const combinedLabels = [];
    const yAxisArray = [];
    const udmArray = [];
    const axisDatasets = this.datasets.filter((dataset) => !this.isDirectionDataset(dataset)); // eventually remove this condition if you want direction datasets to also be assigned to y-axes

    // if (this.datasets.length > 1) {
    if (axisDatasets.length > 1) {
      //Popola il vettore udmArray con le stringhe delle udm
      Array.from(
        new Set(
          // this.datasets
          axisDatasets
            .map((dataset) => {
              const match = dataset.label.match(/\(([^)]+)\)/);

              return match ? match[1] : dataset.label;
            })
            .filter((label) => label !== null)
        )
      ).slice(0, 2).forEach(udm => {
        udmArray.push(udm);
      });

      //Popola il vettore combinedLabels
      udmArray.forEach((udm) => {
        // const matchingLabels = this.datasets
        const matchingLabels = axisDatasets
          .filter((dataset) => dataset.label === udm || dataset.label.includes(`(${udm})`))
          .map((dataset) => dataset.label);

        const joinedLabels = matchingLabels.join(' / ');
        combinedLabels.push(joinedLabels);
      });

      //Popola il vettore yAxisArray
      this.datasets.forEach((data) => {
        if (this.isDirectionDataset(data)) {
          yAxisArray.push('direction-axis');

          return;
        } // eventually remove this condition if you want direction datasets to also be assigned to y-axes

        const index = udmArray.findIndex(elem => data.label.includes(`(${elem})`) || data.label === elem);
        yAxisArray.push(`y-axis-${index + 1}`);
      });

      this.options.scales.yAxes = [];
      udmArray.forEach((udm, i) => {
        this.options.scales.yAxes[i] = {
          display: true,
          id: `y-axis-${i + 1}`,
          position: i == 0 ? 'left' : 'right',
          scaleLabel: {
            display: true,
            labelString: combinedLabels[i]
          },
          gridLines: {
            display: i == 0,
            drawTicks: i == 0
          }
        };
      });

    } else {
      // this.datasets.forEach((e, i) => yAxisArray.push(`y-axis-${i + 1}`));
      this.datasets.forEach((data) => {
        yAxisArray.push(this.isDirectionDataset(data) ? 'direction-axis' : 'y-axis-1');
      });
      this.options.scales.yAxes = [{
        id: 'y-axis-1',
        display: axisDatasets.length > 0,
        position: 'left',
        ticks: {
          display: axisDatasets.length > 0
        },
        scaleLabel: {
          display: axisDatasets.length > 0,
          labelString: axisDatasets[0] ? axisDatasets[0].label : ''
        },
        gridLines: {
          drawTicks: true,
          display: true
        }
      }]; // eventually remove this condition if you want direction datasets to also be assigned to y-axes
    }

    if (this.datasets.some(dataset => this.isDirectionDataset(dataset))) {
      this.options.scales.yAxes.push({
        id: 'direction-axis',
        display: false,
        ticks: {
          min: 0,
          max: 360
        }
      });
    } // eventually remove this condition if you want direction datasets to also be assigned to y-axes

    const labelTimestamps = this.table_chart.rasterVectorChartLabels.map(l => new Date(l).getTime());

    // Calcolo timestep reale
    const timeStepMsVector = [];
    this.table_chart.rasterVectorChartDataSetBar.forEach((dataset) => {
      let lastDataTimeStamp = null;
      let timeStepMs = Infinity;

      for (let i = 0;i < labelTimestamps.length;i++) {
        if (dataset.data[i] !== null) {
          if (lastDataTimeStamp) {
            const diff = Math.abs(labelTimestamps[i] - lastDataTimeStamp);

            if (diff > 0 && diff < timeStepMs) {
              timeStepMs = diff;
            }
          }

          lastDataTimeStamp = labelTimestamps[i];
        }
      }

      timeStepMsVector.push(timeStepMs);
    });

    // Creazione array label Timestamp completo basato sul passo minimo reale
    const minTimeStepMs = Math.min(...timeStepMsVector.filter(step => Number.isFinite(step) && step > 0));
    const completeLabelTimestamps = [];

    if (labelTimestamps.length && Number.isFinite(minTimeStepMs)) {
      const sortedTimestamps = [...labelTimestamps].sort((a, b) => a - b);
      const startTs = sortedTimestamps[0];
      const endTs = sortedTimestamps[sortedTimestamps.length - 1];

      for (let ts = startTs;ts <= endTs;ts += minTimeStepMs) {
        completeLabelTimestamps.push(ts);
      }
    } else {
      completeLabelTimestamps.push(...labelTimestamps);
    }

    // Invece di {x: timestamp, y: val}, usa solo i valori y
    const formattedDatasets = this.table_chart.rasterVectorChartDataSetBar.map((dataset, index) => {
      const yData = completeLabelTimestamps.map((timestep) => {
        const dataIndex = labelTimestamps.indexOf(timestep);

        return dataIndex !== -1 ? dataset.data[dataIndex] : null;
      });

      return {
        ...dataset,
        type: this.isDirectionDataset(dataset) ? 'line' : undefined,
        data: yData, // array semplice, non {x,y}
        yAxisID: yAxisArray[index]
      };
    });

    this.chartdata = {
      labels: completeLabelTimestamps,
      datasets: formattedDatasets
    };

    if (this.mobileBreakpoints()) {
      this.options.tooltips.enabled = false;
      this.options.tooltips.custom = this.externalTooltipHandler;
    }

    this.setXlabels(completeLabelTimestamps, minTimeStepMs);
    this.$emit('chart_data', this.chartdata);
    this.separateDaysPlugin();
    this.drawPreferredGridlinesPlugin();
    this.hideDirectionDatasetsPlugin();
    this.selectionHighlightPlugin();
    this.drawArrowsPlugin();
    this.renderChart(this.chartdata, this.options);
    this.emitLegendData();
    this.setupArrowOverlay();
    this.addEventListeners();
  },
  methods: {
    isLandscape() {
      return /Mobi|Android/i.test(navigator.userAgent) && window.innerHeight < window.innerWidth;
    },
    mobileBreakpoints() {
      return /Mobi|Android/i.test(navigator.userAgent) || this.isLandscape();
    },
    externalTooltipHandler(tooltipModel) {
      if (!tooltipModel || tooltipModel.opacity === 0 || !tooltipModel.body) {
        return;
      }

      const items = tooltipModel.body.map(({ lines }, i) => ({
        text: lines.join(' '),
        color: this.getDatasetColor(this.chartdata.datasets[tooltipModel.dataPoints?.[i]?.datasetIndex])
      })).filter(item => item.text);

      this.$emit('tooltip_data', {
        title: (tooltipModel.title || []).join(' '),
        items
      });
    },
    isDirectionDataset(dataset) {
      return dataset?.udm?.trim() === '°';
    },
    emitLegendData() {
      const chart = this._chart || this.$data._chart;

      if (!chart) {
        return;
      }

      this.$emit('legend_data', chart.data.datasets.map((dataset, index) => ({
        datasetIndex: index,
        label: dataset.label,
        color: this.getDatasetColor(dataset),
        hidden: !chart.isDatasetVisible(index)
      })));
    },
    toggleDataset(index) {
      const chart = this._chart || this.$data._chart;

      if (!chart) {
        return;
      }

      const meta = chart.getDatasetMeta(index);
      meta.hidden = chart.isDatasetVisible(index) ? true : null;
      chart.update();
      this.emitLegendData();
    },
    selectionHighlightPlugin() {
      this.addPlugin({
        afterDatasetsDraw: chart => {
          const activeElements = chart.tooltip?._active;

          if (!activeElements || !activeElements.length) {
            return;
          }

          const index = activeElements[0]._index;
          const xAxis = chart.scales['x-axis-1'];
          const chartArea = chart.chartArea;
          const ctx = chart.ctx;
          const labelsCount = chart.data.labels?.length || 1;
          const slotWidth = xAxis.width / labelsCount;
          const centerX = xAxis.getPixelForTick(index);

          ctx.save();
          ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
          ctx.fillRect(centerX - slotWidth / 2, chartArea.top, slotWidth, chartArea.bottom - chartArea.top);
          ctx.beginPath();
          ctx.moveTo(centerX, chartArea.top);
          ctx.lineTo(centerX, chartArea.bottom);
          ctx.lineWidth = 1;
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
          ctx.setLineDash([4, 4]);
          ctx.stroke();
          ctx.restore();
        }
      });
    },
    hideDirectionDatasetsPlugin() {
      this.addPlugin({
        beforeDatasetDraw: (chart, args) => {
          const dataset = chart.data.datasets[args.index];

          if (this.isDirectionDataset(dataset)) {
            return false;
          }
        }
      });
    },
    addEventListeners() {
      this.$refs.canvas.addEventListener('mousemove', () => {
        if (!this.mouseEntered) {
          this.mouseEntered = true;
          this.redrawArrows();
        }
      });
      this.$refs.canvas.addEventListener('mouseout', () => {
        if (this.mouseEntered) {
          this.mouseEntered = false;
          this.redrawArrows();
        }
      });
    },
    setupArrowOverlay() {
      const chart = this._chart || this.$data._chart;
      const canvas = this.$refs.canvas;

      if (!chart || !canvas || this.arrowCanvas) {
        return;
      }

      const parent = canvas.parentElement;

      if (!parent) {
        return;
      }

      parent.style.position = 'relative';

      const arrowCanvas = document.createElement('canvas');
      arrowCanvas.width = canvas.width;
      arrowCanvas.height = canvas.height;
      arrowCanvas.style.position = 'absolute';
      arrowCanvas.style.top = '0';
      arrowCanvas.style.left = '0';
      arrowCanvas.style.width = '100%';
      arrowCanvas.style.height = '100%';
      arrowCanvas.style.pointerEvents = 'none';
      arrowCanvas.style.opacity = '1';
      arrowCanvas.style.transition = 'opacity 0.3s ease';

      parent.appendChild(arrowCanvas);
      this.arrowCanvas = arrowCanvas;
      this.drawArrows(chart);
    },
    setArrowOverlayOpacity(opacity) {
      if (this.arrowCanvas) {
        this.arrowCanvas.style.opacity = opacity;
      }
    },
    redrawArrows() {
      const chart = this._chart || this.$data._chart;

      if (chart) {
        chart.draw();
      }
    },
    getArrowImage(color = '#000000') {
      if (this.arrowImage[color]) {
        return this.arrowImage[color];
      }

      const svgCode = `
        <svg width="13px" height="13px" viewBox="0 0 0.281 0.281" xmlns="http://www.w3.org/2000/svg" id="arrow" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" transform="matrix(6.123233995736766e-17,1,-1,6.123233995736766e-17,0,0)">
          <path fill="none" stroke="#ffffff" stroke-width="0.02" stroke-linejoin="round" d="M0.155 0.043a0.019 0.019 0 0 1 0.027 0l0.084 0.084a0.019 0.019 0 0 1 0 0.027l-0.084 0.084a0.019 0.019 0 0 1 -0.027 -0.027L0.206 0.159H0.028a0.019 0.019 0 0 1 0 -0.037H0.206L0.155 0.07a0.019 0.019 0 0 1 0 -0.027Z"/>
          <path fill="${color}" d="M0.155 0.043a0.019 0.019 0 0 1 0.027 0l0.084 0.084a0.019 0.019 0 0 1 0 0.027l-0.084 0.084a0.019 0.019 0 0 1 -0.027 -0.027L0.206 0.159H0.028a0.019 0.019 0 0 1 0 -0.037H0.206L0.155 0.07a0.019 0.019 0 0 1 0 -0.027Z"/>
        </svg>
      `;
      const img = new Image();
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgCode)}`;
      this.arrowImage[color] = img;

      return img;
    },
    drawArrows(chart) {
      const directionDatasetsInfo = this.getDirectionDatasetsInfo(chart);

      if (!directionDatasetsInfo.length) {
        return;
      }

      const xAxis = chart.scales['x-axis-1'];
      const yAxis = chart.scales['y-axis-1'];
      const ctx = chart.ctx;
      const arrowOpacity = 1;
      // const arrowOpacity = this.mouseEntered ? 0.3 : 1;
      const arrowImages = directionDatasetsInfo.map(({ dataset }) => {
        return this.getArrowImage(this.getDatasetColor(dataset));
      });

      const draw = () => {
        directionDatasetsInfo.forEach(({ dataset: directionDataset }, rowIndex) => {
          const arrowImage = arrowImages[rowIndex];

          xAxis.ticks.forEach((tick, index) => {
            const labelValue = Number(chart.data.labels[index]);
            const startValue = this._xAxisStartValue != null ? this._xAxisStartValue : labelValue;

            if (this._preferredXAxisStep && Number.isFinite(labelValue) && ((labelValue - startValue) % this._preferredXAxisStep) !== 0) {
              return;
            }

            const svgX = xAxis.getPixelForTick(index) - 8;
            const svgY = yAxis.top + 20 + rowIndex * 24;
            const value = directionDataset.data[index];

            if (value == null || value === '') {
              return;
            }

            const rotationAngle = value * Math.PI / 180;

            ctx.save();
            ctx.globalAlpha = arrowOpacity;
            ctx.translate(svgX + 8, svgY + 8);
            ctx.rotate(rotationAngle);
            ctx.drawImage(arrowImage, -8, -8, 16, 16);
            ctx.restore();
          });
        });
      };

      if (arrowImages.every(image => image.complete)) {
        draw();
      } else {
        arrowImages.forEach(image => {
          image.onload = () => {
            chart.draw();
          };
        });
      }
    },
    getDirectionDatasetsInfo(chart) {
      return chart.data.datasets.reduce((directionDatasets, dataset, datasetIndex) => {
        if (this.isDirectionDataset(dataset) && chart.isDatasetVisible(datasetIndex)) {
          directionDatasets.push({
            dataset,
            datasetIndex
          });
        }

        return directionDatasets;
      }, []);
    },
    getDatasetColor(dataset) {
      if (!dataset) {
        return '#000000';
      }

      const color = dataset.borderColor || dataset.backgroundColor;

      return Array.isArray(color) ? color[0] : color || '#000000';
    },
    separateDaysPlugin() {
      this.addPlugin({
        beforeDatasetsDraw: chart => {
          const ctx = chart.ctx;
          const xAxis = chart.scales['x-axis-1'];
          const yAxis = chart.scales['y-axis-1'];
          const chartArea = chart.chartArea;

          if (!xAxis || !chartArea) {
            return;
          }

          const xMin = xAxis.min;
          const xMax = xAxis.max;

          if (xMin == null || xMax == null) {
            return;
          }

          let dayStart = new Date();
          const localDate = new Date(xMin);

          if (this.isDailyData) {
            dayStart = new Date(Date.UTC(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate()));
          } else {
            dayStart = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 0, 0, 0, 0);
          }

          let currentDayStart = dayStart.getTime();

          if (currentDayStart > xMin) {
            currentDayStart -= 24 * 60 * 60 * 1000;
          }

          const colors = [
            'rgba(255, 255, 255, 1)',
            'rgba(230, 246, 236, 1)'
          ];

          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';

          let dayIndex = 0;

          while (currentDayStart < xMax) {
            const nextDayStart = currentDayStart + 24 * 60 * 60 * 1000;

            const startValue = Math.max(currentDayStart, xMin);
            const endValue = Math.min(nextDayStart, xMax);
            const startX = xAxis.getPixelForValue(startValue);
            const endX = xAxis.getPixelForValue(endValue);

            ctx.fillStyle = colors[dayIndex % colors.length];
            ctx.fillRect(startX, yAxis ? yAxis.top : chartArea.top, endX - startX, (yAxis ? yAxis.bottom : chartArea.bottom) - (yAxis ? yAxis.top : chartArea.top));

            currentDayStart = nextDayStart;
            dayIndex += 1;
          }

          ctx.restore();
        }
      });
    },
    getFormattedDate(timestamp) {
      const date = getFormattedCurrentDate(new Date(timestamp));

      return this.isDailyData ? date.split(' ')[0] : date;
    },
    setXlabels(completeLabelTimestamps, minTimeStepMs) {
      // Calcolo del passo preferito per l'asse X e configurazione dei tick di conseguenza
      this._preferredXAxisStep = null;
      this._xAxisStartValue = Math.min(...completeLabelTimestamps);

      this.options.scales.xAxes[0].afterBuildTicks = (axis) => {
        try {
          const preferredStep = this.getPreferredXAxisStep(axis, minTimeStepMs);
          this._preferredXAxisStep = preferredStep;
          this._xAxisStartValue = axis.min != null ? axis.min : this._xAxisStartValue;
        } catch (e) {
          // fallback: non blocchiamo il grafico
          this._preferredXAxisStep = this._preferredXAxisStep || minTimeStepMs;
        }
      };

      this.options.scales.xAxes[0].ticks.userCallback = (value) => {
        const preferredStepLocal = this._preferredXAxisStep || minTimeStepMs;
        const startValueLocal = this._xAxisStartValue || Math.min(...completeLabelTimestamps);

        if (((value - startValueLocal) % preferredStepLocal) !== 0) {
          return '';
        }

        return this.getFormattedDate(value); // Formattazione completa con data e ora
      };
    },
    drawPreferredGridlinesPlugin() {
      this.addPlugin({
        beforeDatasetsDraw: chart => {
          const ctx = chart.ctx;
          const xAxis = chart.scales['x-axis-1'];
          const chartArea = chart.chartArea;

          if (!xAxis || !chartArea) {
            return;
          }

          const preferredStep = this._preferredXAxisStep;
          const startValue = this._xAxisStartValue != null ? this._xAxisStartValue : xAxis.min;

          if (!preferredStep) {
            return;
          }

          ctx.save();
          ctx.strokeStyle = 'rgba(0,0,0,0.08)';
          ctx.lineWidth = 1;

          const labels = chart.data && chart.data.labels ? chart.data.labels : [];

          labels.forEach((labelValue, idx) => {
            const val = Number(labelValue);

            if (Number.isNaN(val)) {
              return;
            }

            if (((val - startValue) % preferredStep) === 0) {
              const x = xAxis.getPixelForTick(idx);
              ctx.beginPath();
              ctx.moveTo(x, chartArea.top);
              ctx.lineTo(x, chartArea.bottom);
              ctx.stroke();
            }
          });

          ctx.restore();
        }
      });
    },
    getPreferredXAxisStep(axis, minTimeStepMs) {
      const maxTicks = Math.max(2, Math.floor((axis.width || 400) / 20));
      const totalSpan = axis.max - axis.min;
      const candidates = this.PREFERRED_STEPS_MS.filter(step => step >= minTimeStepMs);

      for (let i = 0;i < candidates.length;i += 1) {
        const step = candidates[i];

        if (Math.ceil(totalSpan / step) <= maxTicks) {
          return step;
        }
      }

      return candidates.length ? candidates[candidates.length - 1] : minTimeStepMs;
    },
    drawArrowsPlugin() {
      this.addPlugin({
        afterDatasetsDraw: chart => {
          this.drawArrows(chart);
        }
      });
    }
  }
};
</script>
